import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { useLogout, useNotify, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  ZodPostVerifyPhoneNumberRequest,
  ZodPostVerifyPhoneNumberWithCodeRequest,
} from '@Common/types/bo_external/auth/request_email';
import { isUnsupportedPhoneNumber } from '@Common/types/zod/zod-phone-number';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';
import { PhoneNumberWithDialCodeInput } from '@sh4res/bo-common/fields/PhoneNumberWithDialCodeInput';

import { AddAlternateEmailPageForm } from '@Components/AddAlternateEmailPageForm';
import { AxaTypography } from '@Components/AxaTypography';
import { EnterpriseCodeInfoButton } from '@Components/FirstConnection/EnterpriseCodeInfoButton';
import { LoginPageCTA, LoginPageCTASecondary } from '@Components/LoginPageCTA';
import { VerifyOtpPageForm } from '@Components/VerifyOtpPageForm';
import { TranslationKeys } from '@I18n/types';
import { authClient, getAuthSession } from '@Utils/authClient';

const UNSUPPORTED_PHONE_NUMBER_REASON = 'UNSUPPORTED_PHONE_NUMBER';

function isUnsupportedPhoneNumberSendOtpResponse(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    'reason' in data &&
    data.reason === UNSUPPORTED_PHONE_NUMBER_REASON
  );
}

const VerifyPhoneNumberPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
  max-width: min-content;
`;

function PhoneNumberForm({
  initialPhoneNumber,
  askCompanyCode = false,
  onOTP,
  onContactSupport,
  onUnsupportedPhone,
}: {
  initialPhoneNumber: string | undefined;
  askCompanyCode?: boolean;
  onOTP: (phoneNumber: string) => void;
  onContactSupport: () => void;
  onUnsupportedPhone: () => void;
}) {
  const translate = useTranslate();
  const notify = useNotify();
  const testId = 'BoExternalPhoneNumberForm';

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    mode: 'onBlur',
    resolver: zodResolver(
      askCompanyCode ? ZodPostVerifyPhoneNumberWithCodeRequest : ZodPostVerifyPhoneNumberRequest,
    ),
    defaultValues: { phoneNumber: initialPhoneNumber ?? '', code: null },
  });

  const [loading, setLoading] = useState(false);

  const onSubmit = async (formData: unknown) => {
    const { phoneNumber, code } = formData as { phoneNumber: string; code: string | null };
    const isUnsupported = isUnsupportedPhoneNumber(phoneNumber);
    setLoading(true);
    try {
      const { error, data } = await authClient.phoneNumber.sendOtp({
        phoneNumber,
        companyCode: G.isNotNullable(code) ? parseInt(code) : null,
      });
      if (isUnsupported || isUnsupportedPhoneNumberSendOtpResponse(data)) {
        setLoading(false);
        onUnsupportedPhone();
        return;
      }
      if (error) {
        // oxlint-disable-next-line no-console
        // eslint-disable-next-line no-console
        console.error(error);
        throw new Error(error.message ?? 'OTP sending error');
      }
      onOTP(phoneNumber);
    } catch (error) {
      if (isUnsupported) {
        setLoading(false);
        onUnsupportedPhone();
        return;
      }
      setLoading(false);

      // oxlint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error(error);
      notify('login_page.messages.verify_phone_number.failed', { type: 'error' });
    }
  };

  const rawHeaderText: TranslationKeys = match({
    askCompanyCode,
    hasInitialPhoneNumber: !!initialPhoneNumber,
  })
    .with(
      { askCompanyCode: true },
      () => 'login_page.messages.verify_phone_number.header.code_and_phone' as const,
    )
    .with(
      { hasInitialPhoneNumber: true },
      () => 'login_page.messages.verify_phone_number.header.verify' as const,
    )
    .with(
      { hasInitialPhoneNumber: false },
      () => 'login_page.messages.verify_phone_number.header.add' as const,
    )
    .exhaustive();

  const descriptionText: TranslationKeys = match({
    askCompanyCode,
    hasInitialPhoneNumber: !!initialPhoneNumber,
  })
    .with(
      { askCompanyCode: true },
      () => 'login_page.messages.verify_phone_number.subheader.code_and_phone' as const,
    )
    .with(
      { hasInitialPhoneNumber: true },
      () => 'login_page.messages.verify_phone_number.subheader.verify' as const,
    )
    .with(
      { hasInitialPhoneNumber: false },
      () => 'login_page.messages.verify_phone_number.subheader.add' as const,
    )
    .exhaustive();

  return (
    <>
      <Box position="absolute" top={24} right={24}>
        <CustomLocalesMenuButton testId={`${testId}_LocalesMenuButton`} />
      </Box>
      <PageTitle title={translate(rawHeaderText)} />
      <VerifyPhoneNumberPageFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <TypographyTranslated
          label={rawHeaderText}
          variant="h5"
          component={AxaTypography}
          sx={{ mt: '40px', px: '24px', maxWidth: '400px' }}
        />
        <TypographyTranslated
          label={descriptionText}
          variant="body1"
          sx={{ my: '16px', textAlign: 'justify' }}
        />
        <PhoneNumberWithDialCodeInput
          testId={`${testId}_PhoneNumberInput`}
          label={translate('login_page.messages.form.phone_number_placeholder')}
          value={initialPhoneNumber ?? ''}
          disabled={!!initialPhoneNumber}
          onChange={(full) => setValue('phoneNumber', full, { shouldValidate: true })}
          error={!!errors.phoneNumber}
          helperText={
            G.isNotNullable(errors.phoneNumber?.message)
              ? translate(errors.phoneNumber.message as TranslationKeys)
              : undefined
          }
          sx={{ mb: '24px', width: '100%' }}
        />

        {askCompanyCode && (
          <TextField
            data-testid={`${testId}_EnterpriseCodeInput`}
            label={
              <span>
                {translate('login_page.messages.form.external_code_enterprise_placeholder')}
                <EnterpriseCodeInfoButton testId={`${testId}_EnterpriseCodeInfoButton`} />
              </span>
            }
            variant="outlined"
            sx={{
              borderRadius: '16px',
              mb: '24px',
              width: '100%',
            }}
            defaultValue=""
            placeholder="13749382"
            InputLabelProps={{ shrink: true }}
            error={'code' in errors}
            helperText={
              'code' in errors && G.isNotNullable(errors.code?.message)
                ? translate(errors.code.message as TranslationKeys)
                : null
            }
            {...register('code')}
          />
        )}

        <Stack direction="row" gap={2} justifyContent="end">
          {initialPhoneNumber && (
            <LoginPageCTASecondary onClick={onContactSupport} type="button" variant="contained">
              <TypographyTranslated
                component={AxaTypography}
                sx={{ textWrap: 'nowrap' }}
                label="login_page.messages.verify_phone_number.contact_us"
              />
            </LoginPageCTASecondary>
          )}
          <LoginPageCTA
            data-testid={`${testId}_SubmitButton`}
            type="submit"
            variant="contained"
            disabled={!isValid || loading}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.verify_phone_number.cta_text"
            />
          </LoginPageCTA>
        </Stack>
      </VerifyPhoneNumberPageFormWrapper>
    </>
  );
}

function ContactPage({ onContinue }: { onContinue: () => void }) {
  const translate = useTranslate();
  const rawHeaderText: TranslationKeys = 'login_page.messages.contact_us.header';

  return (
    <>
      <PageTitle title={translate(rawHeaderText)} />
      <VerifyPhoneNumberPageFormWrapper>
        <Stack direction="row" gap={2} justifyContent="center">
          <svg
            width="63"
            height="63"
            viewBox="0 0 63 63"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.99958 1.80678C10.3399 -1.04113 15.3936 -0.453745 17.9924 3.0851C18.1711 3.3286 18.371 3.65568 18.773 4.30613L22.4576 10.268C23.5044 11.9619 24.0294 12.8102 24.2444 13.6483C24.6639 15.2857 24.3309 17.0249 23.3385 18.3927C22.458 19.6057 21.0496 20.4094 19.8723 21.311C19.0271 22.0136 19.0965 23.0859 19.5465 24.0145H19.5393C21.5682 28.6439 34.9615 41.9569 39.133 43.0747C40.1948 43.2909 40.8371 42.6109 41.4068 41.8465C42.5963 40.2505 43.1917 39.4505 43.8918 38.9425C45.2599 37.9504 46.9988 37.6201 48.6363 38.0402C49.4744 38.2552 50.3227 38.7766 52.0165 39.8234L57.9784 43.508C58.6303 43.9108 58.9558 44.1132 59.1995 44.2921C62.7383 46.8909 63.3257 51.9446 60.4778 55.285C60.2817 55.5148 60.0093 55.7862 59.468 56.327C58.7659 57.0284 58.4147 57.3812 57.9784 57.7664C52.0897 62.9652 42.4996 63.7213 35.6955 59.7931C25.6199 53.9759 8.28974 36.6684 2.46996 26.589C1.40649 24.747 0.702071 22.6962 0.325104 20.5734C0.292817 20.3935 0.259426 20.2136 0.232005 20.0327C-0.00882099 18.4224 -0.0657313 16.7765 0.0780336 15.1486C0.419391 11.1487 1.90901 7.26141 4.51814 4.30613C4.90355 3.86964 5.25552 3.51573 5.95759 2.81296C6.49801 2.27201 6.76982 2.00272 6.99958 1.80678Z"
              fill="#00008F"
            />
          </svg>
        </Stack>

        <TypographyTranslated
          label={rawHeaderText}
          component={AxaTypography}
          variant="h5"
          align="center"
          sx={{ mt: 5, px: 3 }}
        />

        <TypographyTranslated
          label="login_page.messages.contact_us.content"
          variant="body2"
          align="center"
          sx={{ my: 2 }}
        />

        <Stack direction="row" gap={2} justifyContent="center">
          <LoginPageCTASecondary
            onClick={onContinue}
            variant="contained"
            sx={{ width: 'max-content', px: 3 }}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.contact_us.cta_text"
            />
          </LoginPageCTASecondary>
        </Stack>
      </VerifyPhoneNumberPageFormWrapper>
    </>
  );
}

type State =
  | { page: 'loading' }
  | {
      page: 'phoneNumber';
      askCompanyCode?: boolean;
      initialPhoneNumber?: string;
    }
  | {
      page: 'contact';
    }
  | {
      page: 'addAlternateEmail';
    }
  | {
      page: 'otp';
      phoneNumber: string;
    };

export function VerifyPhoneNumberPageForm() {
  const navigate = useNavigate();
  const logout = useLogout();
  const location = useLocation();
  const locationState = location.state as { nextPathname: string; nextSearch: string } | undefined;
  const nextPathName = locationState?.nextPathname;
  const nextSearch = locationState?.nextSearch;

  const [state, setState] = useState<State>({ page: 'loading' });

  useEffect(() => {
    (async () => {
      const { data } = await getAuthSession();
      if (!data || data.user.phoneNumberVerified || data.user.alternateEmail) {
        navigate(`${nextPathName ?? ''}${nextSearch ?? ''}` || '/', { replace: true });
        return;
      }

      const phoneNumber = data.user.phoneNumber ?? undefined;
      if (phoneNumber && isUnsupportedPhoneNumber(phoneNumber)) {
        setState({ page: 'addAlternateEmail' });
        return;
      }

      setState({ page: 'phoneNumber', initialPhoneNumber: phoneNumber });
    })();
  }, [navigate, nextPathName, nextSearch]);

  if (state.page === 'phoneNumber') {
    return (
      <PhoneNumberForm
        initialPhoneNumber={state.initialPhoneNumber}
        askCompanyCode={state.askCompanyCode}
        onOTP={(phoneNumber) => setState({ page: 'otp', phoneNumber })}
        onContactSupport={() => setState({ page: 'contact' })}
        onUnsupportedPhone={() => setState({ page: 'addAlternateEmail' })}
      />
    );
  }

  if (state.page === 'contact') {
    return (
      <ContactPage onContinue={() => setState({ page: 'phoneNumber', askCompanyCode: true })} />
    );
  }

  if (state.page === 'addAlternateEmail') {
    return (
      <AddAlternateEmailPageForm
        onBackToLogin={() => {
          // Must go through authProvider logout so isLoggedIn/cache are cleared.
          // Calling authClient.signOut() alone leaves RA thinking the user is
          // authenticated and redirects to a blank authenticated shell.
          void logout();
        }}
      />
    );
  }

  if (state.page === 'otp') {
    return (
      <VerifyOtpPageForm
        showLanguagePicker={false}
        type="sms"
        send={async () => authClient.phoneNumber.sendOtp({ phoneNumber: state.phoneNumber })}
        verify={async (code, fetchOptions) => authClient.phoneNumber.verify({ code }, fetchOptions)}
        onSuccess={() => {
          navigate(`${nextPathName ?? ''}${nextSearch ?? ''}` || '/', { replace: true });
        }}
        onExpired={() => window.location.reload()}
      />
    );
  }

  return null;
}
